<template>
    <div>

        <Head :title="__('Audit Trail - Ticket #') + ticket.uid" />

        <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-bold text-gray-900">
                    {{ __('Audit Trail - Ticket #') }}{{ ticket.uid }}
                </h1>
                <Link :href="route('tickets.edit', ticket.id)" class="btn-indigo">
                {{ __('Back to Ticket') }}
                </Link>
            </div>

            <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                        {{ ticket.subject }}
                    </h3>
                    <p class="mt-1 max-w-2xl text-sm text-gray-500">
                        {{ __('Created') }}: {{ formatDate(ticket.created_at) }}
                        <span v-if="ticket.closed_at" class="ml-4">
                            {{ __('Closed') }}: {{ formatDate(ticket.closed_at) }}
                        </span>
                    </p>
                </div>

                <div class="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
                    <ul class="divide-y divide-gray-200">
                        <li v-for="activity in activities" :key="activity.id" class="px-4 py-4 sm:px-6">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <div
                                        class="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                                        {{ activity.user_id ? activity.user.first_name.charAt(0) + activity.user.last_name.charAt(0) : 'S' }}
                                    </div>
                                    <div>
                                        <p class="text-sm font-medium text-gray-900">
                                            {{ activity.user_id ? activity.user.first_name + ' ' + activity.user.last_name : 'System' }}
                                        </p>
                                        <p class="text-sm text-gray-500">
                                            {{ formatDate(activity.created_at) }}
                                        </p>
                                    </div>
                                </div>
                                <div class="ml-4 flex-shrink-0">
                                    <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="activityColor(activity.description)">
                                        {{ activity.description }}
                                    </span>
                                </div>
                            </div>
                            <div class="mt-2 text-sm text-gray-700" v-if="activity.changes">
                                <div v-for="(change, field) in formatChanges(activity.changes)" :key="field"
                                    class="mb-1" v-if="change && typeof change === 'object'">
                                    <span class="font-semibold">{{ fieldLabel(field) }}:</span>
                                    <span v-if="change.old !== undefined && change.old !== null"
                                        class="text-red-600 line-through mr-1">
                                        {{ formatChangeValue(field, change.old) }}
                                    </span>
                                    <span v-if="change.new !== undefined && change.new !== null" class="text-green-600">
                                        {{ formatChangeValue(field, change.new) }}
                                    </span>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Shared/Layout';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditor from '@ckeditor/ckeditor5-vue';


export default {
    components: { Head, Link, ClassicEditor, ckeditor: CKEditor.component, },
    layout: Layout,
    props: {
        ticket: Object,
        activities: Array,
    },
    methods: {
        formatDate(date) {
            return new Date(date).toLocaleString();
        },
        activityColor(description) {
            const desc = description.toLowerCase();
            if (desc.includes('closed')) return 'bg-red-100 text-red-800';
            if (desc.includes('created')) return 'bg-green-100 text-green-800';
            if (desc.includes('assigned')) return 'bg-purple-100 text-purple-800';
            if (desc.includes('status')) return 'bg-blue-100 text-blue-800';
            if (desc.includes('priority')) return 'bg-yellow-100 text-yellow-800';
            return 'bg-gray-100 text-gray-800';
        },
        fieldLabel(field) {
            // Ensure field is a string and handle unexpected input
            if (typeof field !== 'string') {
                console.warn('Invalid field type:', field);
                return 'Field';
            }

            const labels = {
                'status_id': 'Status',
                'priority_id': 'Priority',
                'assigned_to': 'Assigned To',
                'category_id': 'Category',
                'sub_category_id': 'Subcategory',
                'department_id': 'Department',
                'due': 'Due Date',
                'resolution': 'Resolution',
                'subject': 'Subject',
                'details': 'Details'
            };

            return labels[field] || field;
        },

        formatChanges(changes) {
            try {
                // If changes is already an object, return it
                if (typeof changes === 'object' && !Array.isArray(changes)) {
                    return changes;
                }

                // If changes is a string, try to parse it
                if (typeof changes === 'string') {
                    return JSON.parse(changes);
                }

                // Fallback for unexpected formats
                console.warn('Unexpected changes format:', changes);
                return {};
            } catch (e) {
                console.error('Error parsing changes:', e);
                return {};
            }
        }

    }
    }
</script>
